var _excluded = ["defaultChecked", "isChecked", "isFocusable", "isDisabled", "isReadOnly", "isRequired", "onChange", "isInvalid", "name", "value", "id"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useBoolean, useControllableProp, useId } from '../../hooks';
import { ariaAttr, callAllHandlers, dataAttr, pick, mergeRefs } from '../../utils';
import { visuallyHiddenStyle } from '../visually-hidden';
import { useFormControl, useFormControlContext } from '../form-control';
import { useRadioGroupContext } from './RadioGroup';
import { useListboxContext } from '../listbox';
/**
 * @todo use the `useClickable` hook here
 * to manage the isFocusable & isDisabled props
 */

export function useRadio() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var {
      defaultChecked,
      isChecked: isCheckedProp,
      isFocusable,
      isDisabled,
      isReadOnly,
      isRequired,
      onChange,
      isInvalid: isInvalidProp,
      name,
      value,
      id: idProp
    } = props,
    htmlProps = _objectWithoutProperties(props, _excluded);
  var [isFocused, setFocused] = useBoolean();
  var [isHovered, setHovering] = useBoolean();
  var [isActive, setActive] = useBoolean();
  var ref = useRef(null);
  var [isCheckedState, setChecked] = useState(Boolean(defaultChecked));
  var [isControlled, isChecked] = useControllableProp(isCheckedProp, isCheckedState);
  var inputProps = useFormControl(props);
  var isInvalid = isInvalidProp || inputProps['aria-invalid'];
  var group = useRadioGroupContext();
  var listboxGroup = useListboxContext();
  var formControl = useFormControlContext;
  var isWithinFormControl = !!formControl;
  var isWithinGroup = !!group || !!listboxGroup;
  var uuid = useId();
  var id = isWithinFormControl && !isWithinGroup ? inputProps.id : "radio-".concat(uuid);
  id = idProp !== null && idProp !== void 0 ? idProp : id;
  var handleChange = useCallback(event => {
    if (isReadOnly || isDisabled) {
      event.preventDefault();
      return;
    }
    if (!isControlled) {
      setChecked(event.target.checked);
    }
    onChange === null || onChange === void 0 ? void 0 : onChange(event);
  }, [isControlled, isDisabled, isReadOnly, onChange]);
  var onKeyDown = useCallback(event => {
    if (event.key === ' ') {
      setActive.on();
    }
  }, [setActive]);
  var onKeyUp = useCallback(event => {
    if (event.key === ' ') {
      setActive.off();
    }
  }, [setActive]);
  var getCheckboxProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props), {}, {
      ref,
      'data-active': dataAttr(isActive),
      'data-hover': dataAttr(isHovered),
      'data-disabled': dataAttr(isDisabled),
      'data-invalid': dataAttr(isInvalid),
      'data-checked': dataAttr(isChecked),
      'data-focus': dataAttr(isFocused),
      'data-readonly': dataAttr(isReadOnly),
      'aria-hidden': true,
      onMouseDown: callAllHandlers(props.onMouseDown, setActive.on),
      onMouseUp: callAllHandlers(props.onMouseUp, setActive.off),
      onMouseEnter: callAllHandlers(props.onMouseEnter, setHovering.on),
      onMouseLeave: callAllHandlers(props.onMouseLeave, setHovering.off)
    });
  }, [isActive, isHovered, isDisabled, isInvalid, isChecked, isFocused, isReadOnly, setActive.on, setActive.off, setHovering.on, setHovering.off]);
  var getInputProps = useCallback(function () {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var forwardedRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    var ownProps = pick(inputProps, ['id', 'disabled', 'readOnly', 'required', 'aria-invalid', 'aria-required', 'aria-describedby', 'onFocus', 'onBlur']);

    // This is a workaround for React Concurrent Mode issue https://github.com/facebook/react/issues/18591. Remove once it's fixed.
    var focus = () => {
      if (typeof ReactDOM.flushSync === 'function') {
        ReactDOM.flushSync(() => {
          setFocused.on();
        });
      } else {
        setFocused.on();
      }
    };
    var trulyDisabled = ownProps.disabled && !isFocusable;
    return _objectSpread(_objectSpread(_objectSpread({}, props), ownProps), {}, {
      id,
      ref: mergeRefs(forwardedRef, ref),
      type: 'radio',
      name,
      value,
      onChange: callAllHandlers(props.onChange, handleChange),
      onBlur: callAllHandlers(ownProps.onBlur, props.onBlur, setFocused.off),
      onFocus: callAllHandlers(ownProps.onFocus, props.onFocus, focus),
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onKeyUp: callAllHandlers(props.onKeyUp, onKeyUp),
      checked: isChecked,
      disabled: trulyDisabled,
      'aria-disabled': ariaAttr(trulyDisabled),
      style: visuallyHiddenStyle
    });
  }, [id, inputProps, isFocusable, name, value, handleChange, setFocused, onKeyDown, onKeyUp, isChecked]);
  var getLabelProps = function getLabelProps() {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    return _objectSpread(_objectSpread({}, props), {}, {
      ref,
      onMouseDown: callAllHandlers(props.onMouseDown, stop),
      onTouchStart: callAllHandlers(props.onTouchStart, stop),
      'data-disabled': dataAttr(isDisabled),
      'data-checked': dataAttr(isChecked),
      'data-invalid': dataAttr(isInvalid)
    });
  };
  var getContainerProps = function getContainerProps() {
    var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return _objectSpread(_objectSpread({}, props), {}, {
      'data-disabled': dataAttr(isDisabled),
      'data-checked': dataAttr(isChecked),
      'data-invalid': dataAttr(isInvalid),
      'data-focus': dataAttr(isFocused),
      'data-readonly': dataAttr(isReadOnly)
    });
  };
  return {
    state: {
      isInvalid,
      isFocused,
      isChecked,
      isActive,
      isHovered,
      isDisabled,
      isReadOnly,
      isRequired
    },
    getCheckboxProps,
    getInputProps,
    getLabelProps,
    getContainerProps,
    htmlProps
  };
}

/**
 * Prevent `onBlur` being fired when the checkbox label is touched
 */
function stop(event) {
  event.preventDefault();
  event.stopPropagation();
}